import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IAvatarTrain, IAvatarVideo, AvatarTrainStatus, AvatarVideoStatus, ModelType,
  PaginationResult, PaginationQuery, AvatarTrainQuery, AvatarVideoQuery, CreateAvatarVideoRequest
} from '@packages/global';
import { avatarTrainApi, avatarVideoApi } from '../services/user/avatar.api';
import { useModels } from '../hooks/useModels';
import { useDialog } from '../hooks/useDialog';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType, TaskStatusChangedMessage, TaskType, TaskStatus } from '../services/websocket/types';
import Pagination from '../components/Pagination';
import AvatarVideoForm from '../components/Avatar/AvatarVideoForm';
import AvatarVideoCard from '../components/Avatar/AvatarVideoCard';

const DEFAULT_PAGINATION: PaginationResult = { page: 1, limit: 12, total: 0, totalPages: 0, sortBy: 'createdAt', sortOrder: 'desc' };

export default function AvatarVideosPage() {
  const { t } = useTranslation(['avatar', 'common']);
  const { alert, confirm } = useDialog();
  const { client, isConnected } = useWebSocketContext();

  const [videos, setVideos] = useState<IAvatarVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationResult>(DEFAULT_PAGINATION);
  const [videoForm, setVideoForm] = useState<CreateAvatarVideoRequest>({ avatarId: '', avatarType: 1, audioUrl: '', modelId: '' });
  const [completedTrains, setCompletedTrains] = useState<IAvatarTrain[]>([]);

  const { models: videoModels, loading: videoModelsLoading } = useModels(ModelType.AVATAR_VIDEO);

  useEffect(() => {
    if (videoModels.length > 0 && !videoForm.modelId) {
      setVideoForm(p => ({ ...p, modelId: videoModels[0].id }));
    }
  }, [videoModels]);

  const loadCompletedTrains = useCallback(async () => {
    try {
      const res = await avatarTrainApi.getPageList({
        query: {},
        pagination: { page: 1, limit: 100, sortBy: 'createdAt', sortOrder: 'desc' }
      } as PaginationQuery<AvatarTrainQuery>);
      setCompletedTrains(res.items.filter(tr => tr.status === AvatarTrainStatus.COMPLETED && tr.avatarId));
    } catch { /* ignore */ }
  }, []);

  const loadVideos = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await avatarVideoApi.getPageList({
        query: {},
        pagination: { page, limit: 12, sortBy: 'createdAt', sortOrder: 'desc' }
      } as PaginationQuery<AvatarVideoQuery>);
      setVideos(res.items);
      setPagination(p => ({ ...p, page, total: res.pagination.total, totalPages: res.pagination.totalPages }));
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadVideos();
    loadCompletedTrains();
  }, [loadVideos, loadCompletedTrains]);

  useEffect(() => {
    if (!client || !isConnected) return;
    const statusMap: Record<TaskStatus, string> = {
      [TaskStatus.PENDING]: 'pending', [TaskStatus.SUBMITTED]: 'submitted',
      [TaskStatus.PROCESSING]: 'processing', [TaskStatus.COMPLETED]: 'completed', [TaskStatus.FAILED]: 'failed'
    };
    const unsub = client.onMessage(MessageType.TASK_STATUS_CHANGED, (msg: TaskStatusChangedMessage) => {
      if (msg.taskType !== TaskType.AVATAR_VIDEO) return;
      setVideos(prev => prev.map(v =>
        v.id === msg.taskId ? { ...v, status: statusMap[msg.newStatus] as AvatarVideoStatus } : v
      ));
      if (msg.newStatus === TaskStatus.COMPLETED || msg.newStatus === TaskStatus.FAILED) {
        avatarVideoApi.getById(msg.taskId)
          .then(u => setVideos(prev => prev.map(v => v.id === u.id ? u : v)))
          .catch(() => { });
      }
    });
    return unsub;
  }, [client, isConnected]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoForm.modelId) { await alert(t('avatar:video.selectModel'), '', 'warning'); return; }
    try {
      const created = await avatarVideoApi.create(videoForm);
      setVideos(prev => [created, ...prev]);
      setVideoForm({ avatarId: '', avatarType: 1, audioUrl: '', modelId: videoModels[0]?.id || '' });
    } catch (err: any) { await alert(err.message || t('avatar:video.createFailed'), '', 'danger'); }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!await confirm(t('avatar:video.deleteConfirm', { title: title || id }), t('avatar:video.deleteTitle'), 'danger')) return;
    try { await avatarVideoApi.delete(id); setVideos(prev => prev.filter(v => v.id !== id)); }
    catch { await alert(t('avatar:video.deleteFailed'), '', 'danger'); }
  };

  return (
    <div className="flex h-screen bg-bg-page">
      <AvatarVideoForm
        videoForm={videoForm}
        videoModels={videoModels}
        videoModelsLoading={videoModelsLoading}
        completedTrains={completedTrains}
        onVideoFormChange={data => setVideoForm(p => ({ ...p, ...data }))}
        onVideoSubmit={handleSubmit}
      />

      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="mb-4">
            <h1 className="text-2xl font-semibold text-gray-50">{t('avatar:tabs.videos')}</h1>
            <p className="text-sm text-gray-400 mt-1">{t('avatar:video.subtitle')}</p>
          </div>

          {loading ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <span className="ml-3 text-gray-400">{t('common:loading')}</span>
            </div>
          ) : videos.length === 0 ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-12 text-center text-gray-400">
              {t('avatar:video.empty')}
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
              {videos.map(item => (
                <AvatarVideoCard key={item.id} item={item} onDelete={handleDelete} />
              ))}
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-4 mt-6">
              <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={loadVideos} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
